import { GetStaticPaths, GetStaticProps } from "next";
import { FunctionComponent, useEffect, useState } from "react";
import { Conversion } from "@bluebottlecoffee/design-system";
import sanityClient from "../../../../lib/sanity-client";
import renderModule from "../../../../lib/render-module";
import {
  Product as ProductSchema,
  Config as ConfigSchema,
} from "../../../../lib/sanity-schema";
import {
  toConversionProps,
  ConversionSchema,
} from "../../../../lib/transformers/conversion";
import { useCart } from "../../../../lib/chord/packages/web/src/hooks/actions/use-cart";
import { useProduct } from "../../../../lib/chord/packages/web/src/hooks/actions/use-product";
import useVariantAvailability from "../../../../lib/chord/packages/web/src/hooks/components/use-variant-availability";
import {
  CartBasePropsWithSlug,
  filters,
  cartQueries,
  utils,
} from "../../../../lib/sanity/shared";
import { getDefaultStaticPropsMiniCartWithSlug } from "../../../../lib/utils";
import { LayoutWrapper } from "../../../../components/LayoutWrapper";
import { MiniCartDialog } from "../../../../components/MiniCartDialog";
import { useUser } from "../../../../lib/chord/packages/web/src/hooks/actions/use-user";
import { useAuth } from "../../../../lib/chord/packages/web/src/hooks/actions/use-auth";
import {
  buildSanityQuery,
  productFilters,
} from "../../../../lib/sanity/product-queries";
import { ChordUseCartReturnType } from "../../../../lib/chord/types";

interface PageData extends CartBasePropsWithSlug {
  conversion: ConversionSchema;
}

function isValidConversion(p: ConversionSchema, lang: string): boolean {
  const test: Function = (field: string): boolean => {
    if (!p[field]) {
      if (p.name && p.name[lang]) {
        console.warn(`${p.name[lang]} does not have ${field}, skipping...`);
        return false;
      } else {
        throw Error(
          `Product with sanity id [${p._id}] is published and does not have a name in ${lang}.`,
        );
      }
    } else if (Array.isArray(p[field])) {
      if (p[field].length === 0) {
        if (p.name && p.name[lang]) {
          console.warn(`${p.name[lang]} has an empty ${field}, skipping...`);
          return false;
        } else {
          throw Error(
            `Product with sanity id [${p._id}] is published and does not have a name in ${lang}.`,
          );
        }
      }
    }

    return true;
  };

  // ensure require fields exist
  if (
    !test("slug") ||
    !test("description") ||
    !test("images") ||
    !test("variants")
  )
    return false;

  // multiple variants require option type and value
  if (p.variants.length > 1) {
    // option type check
    if (!p.optionTypes || p.optionTypes.length === 0) {
      console.log(
        `${p.name[lang]} has multiple variants but no optionType, skipping...`,
      );
      return false;
    }

    // option value check
    // eslint-disable-next-line no-lonely-if
    if (
      !p.variants.every((v) => {
        if (!v.optionValues || v.optionValues.length === 0) {
          console.log(
            `${p.name[lang]} has a variant ${v.label[lang]} with no optionValue`,
          );
          return false;
        } else {
          return true;
        }
      })
    )
      return false;
  }

  return true;
}

export const getStaticProps: GetStaticProps<PageData> = async (context) => {
  const { props: defaultProps } = await getDefaultStaticPropsMiniCartWithSlug(
    context,
  );

  const { lang, slug } = defaultProps;

  const query = buildSanityQuery(lang, slug);

  const conversion = await sanityClient().fetch<ConversionSchema>(query);
  if (!isValidConversion(conversion, lang)) return { notFound: true };

  return {
    props: {
      ...defaultProps,
      conversion,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const config = await sanityClient().fetch<ConfigSchema>(
    cartQueries.configQuery,
  );

  const productSlugs = await sanityClient().fetch<
    ProductSchema["slug"]["current"][]
  >(`*[
    ${filters.whereType("product")}
    && ${productFilters}
  ].${utils.getSlug<ProductSchema>("slug")}`);

  const params = config.languages.flatMap((lang) =>
    productSlugs.map((slug) => ({
      params: {
        region: config.region,
        lang,
        slug,
      },
    })),
  );

  return {
    paths: params,
    fallback: false,
  };
};

const Page: FunctionComponent<PageData> = ({
  conversion,
  ...miniCartLayoutWrapperProps
}) => {
  const [isWaitListed, setIsWaitListed] = useState<boolean>(false);
  const [waitListError, setWaitListError] = useState<boolean>(false);
  const { region, lang } = miniCartLayoutWrapperProps;
  const { addToCart, subscribeProduct }: ChordUseCartReturnType = useCart();
  const { createStockRequest } = useProduct();
  const { user, loadUser } = useUser();
  const miniCartDialog = (
    <MiniCartDialog
      miniCartCopy={miniCartLayoutWrapperProps.cart}
      region={region}
      lang={lang}
    />
  );

  async function handleCreateStockRequest({
    email,
    sku,
  }: {
    email: string;
    sku: string;
  }): Promise<void> {
    try {
      const response = await createStockRequest({ email, sku });
      if (response?.status === 201) {
        setIsWaitListed(true);
      } else {
        setWaitListError(true);
      }
    } catch (error) {
      setWaitListError(true);
      console.error(error);
    }
  }

  const { getToken } = useAuth();

  useEffect(() => {
    try {
      getToken();
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    if (!user.data.email) {
      try {
        loadUser();
      } catch (error) {
        console.error(error);
      }
    }
  }, [user.data]);

  return (
    <LayoutWrapper
      {...{
        ...miniCartLayoutWrapperProps,
        pageTitle: conversion.name[lang],
        metaTags: conversion.metaTags,
        slideOutContent: [miniCartDialog],
      }}
    >
      <Conversion
        {...toConversionProps(conversion, lang)}
        addToCart={addToCart}
        createStockRequest={handleCreateStockRequest}
        subscribeProduct={subscribeProduct}
        useVariantAvailability={useVariantAvailability}
        isWaitListed={isWaitListed}
        waitListError={waitListError}
        waitListEmailDefault={user?.data.email}
      />
      {conversion.modules?.map((m) => renderModule(m, region, lang))}
    </LayoutWrapper>
  );
};

export default Page;
